import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Modal from '../components/Modal';
import { 
  Tag, 
  Package, 
  Plus, 
  Edit, 
  Trash2,
  Layers
} from 'lucide-react';

function CategorieTipologie() {
  const [activeTab, setActiveTab] = useState('categorie'); // 'categorie' o 'tipologie'
  
  // Stato categorie
  const [categorie, setCategorie] = useState([]);
  const [loadingCategorie, setLoadingCategorie] = useState(true);
  const [showCategoriaForm, setShowCategoriaForm] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [categoriaFormData, setCategoriaFormData] = useState({ nome: '', descrizione: '', attivo: true });
  const [showDeleteCategoriaModal, setShowDeleteCategoriaModal] = useState(false);
  const [categoriaToDelete, setCategoriaToDelete] = useState(null);

  // Stato tipologie
  const [tipologie, setTipologie] = useState([]);
  const [loadingTipologie, setLoadingTipologie] = useState(true);
  const [showTipologiaForm, setShowTipologiaForm] = useState(false);
  const [editingTipologia, setEditingTipologia] = useState(null);
  const [tipologiaFormData, setTipologiaFormData] = useState({ nome: '', descrizione: '', attivo: true });
  const [showDeleteTipologiaModal, setShowDeleteTipologiaModal] = useState(false);
  const [tipologiaToDelete, setTipologiaToDelete] = useState(null);

  useEffect(() => {
    if (activeTab === 'categorie') {
      loadCategorie();
    } else {
      loadTipologie();
    }
  }, [activeTab]);

  // ============ CATEGORIE ============
  const loadCategorie = async () => {
    setLoadingCategorie(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/categorie/admin/list', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCategorie(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento categorie:', error);
      toast.error('Errore nel caricamento delle categorie');
    } finally {
      setLoadingCategorie(false);
    }
  };

  const handleOpenCategoriaForm = (categoria = null) => {
    if (categoria) {
      setEditingCategoria(categoria);
      setCategoriaFormData({ 
        nome: categoria.nome, 
        descrizione: categoria.descrizione || '', 
        attivo: categoria.attivo === 1 || categoria.attivo === true 
      });
    } else {
      setEditingCategoria(null);
      setCategoriaFormData({ nome: '', descrizione: '', attivo: true });
    }
    setShowCategoriaForm(true);
  };

  const handleCloseCategoriaForm = () => {
    setShowCategoriaForm(false);
    setEditingCategoria(null);
    setCategoriaFormData({ nome: '', descrizione: '', attivo: true });
  };

  const handleSubmitCategoria = async (e) => {
    e.preventDefault();
    
    if (!categoriaFormData.nome.trim()) {
      toast.error('Il nome categoria è obbligatorio');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = {
        ...categoriaFormData,
        attivo: categoriaFormData.attivo === true || categoriaFormData.attivo === 1 ? 1 : 0
      };
      
      if (editingCategoria) {
        await axios.put(`/api/categorie/${editingCategoria.id}`, formDataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Categoria aggiornata con successo');
      } else {
        await axios.post('/api/categorie', formDataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Categoria creata con successo');
      }
      
      handleCloseCategoriaForm();
      loadCategorie();
    } catch (error) {
      console.error('Errore salvataggio categoria:', error);
      toast.error(error.response?.data?.message || 'Errore nel salvataggio categoria');
    }
  };

  const handleDeleteCategoria = (categoria) => {
    setCategoriaToDelete(categoria);
    setShowDeleteCategoriaModal(true);
  };

  const executeDeleteCategoria = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/categorie/${categoriaToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Categoria eliminata definitivamente');
      setShowDeleteCategoriaModal(false);
      setCategoriaToDelete(null);
      loadCategorie();
    } catch (error) {
      console.error('Errore eliminazione categoria:', error);
      toast.error(error.response?.data?.message || 'Errore nell\'eliminazione categoria');
    }
  };

  // ============ TIPOLOGIE ============
  const loadTipologie = async () => {
    setLoadingTipologie(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/tipologie/admin/list', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTipologie(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento tipologie:', error);
      toast.error('Errore nel caricamento delle tipologie');
    } finally {
      setLoadingTipologie(false);
    }
  };

  const handleOpenTipologiaForm = (tipologia = null) => {
    if (tipologia) {
      setEditingTipologia(tipologia);
      setTipologiaFormData({ 
        nome: tipologia.nome, 
        descrizione: tipologia.descrizione || '', 
        attivo: tipologia.attivo === 1 || tipologia.attivo === true 
      });
    } else {
      setEditingTipologia(null);
      setTipologiaFormData({ nome: '', descrizione: '', attivo: true });
    }
    setShowTipologiaForm(true);
  };

  const handleCloseTipologiaForm = () => {
    setShowTipologiaForm(false);
    setEditingTipologia(null);
    setTipologiaFormData({ nome: '', descrizione: '', attivo: true });
  };

  const handleSubmitTipologia = async (e) => {
    e.preventDefault();
    
    if (!tipologiaFormData.nome.trim()) {
      toast.error('Il nome tipologia è obbligatorio');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = {
        ...tipologiaFormData,
        attivo: tipologiaFormData.attivo === true || tipologiaFormData.attivo === 1 ? 1 : 0
      };
      
      if (editingTipologia) {
        await axios.put(`/api/tipologie/${editingTipologia.id}`, formDataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tipologia aggiornata con successo');
      } else {
        await axios.post('/api/tipologie', formDataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tipologia creata con successo');
      }
      
      handleCloseTipologiaForm();
      loadTipologie();
    } catch (error) {
      console.error('Errore salvataggio tipologia:', error);
      toast.error(error.response?.data?.message || 'Errore nel salvataggio tipologia');
    }
  };

  const handleDeleteTipologia = (tipologia) => {
    setTipologiaToDelete(tipologia);
    setShowDeleteTipologiaModal(true);
  };

  const executeDeleteTipologia = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/tipologie/${tipologiaToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tipologia eliminata definitivamente');
      setShowDeleteTipologiaModal(false);
      setTipologiaToDelete(null);
      loadTipologie();
    } catch (error) {
      console.error('Errore eliminazione tipologia:', error);
      toast.error(error.response?.data?.message || 'Errore nell\'eliminazione tipologia');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorie & Tipologie</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestisci le categorie prodotti e le tipologie di misura
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('categorie')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'categorie'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Tag className="inline h-4 w-4 mr-2" />
            Categorie
          </button>
          <button
            onClick={() => setActiveTab('tipologie')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tipologie'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Package className="inline h-4 w-4 mr-2" />
            Tipologie
          </button>
        </nav>
      </div>

      {/* Tab Categorie */}
      {activeTab === 'categorie' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Categorie Prodotti</h2>
            <button
              onClick={() => handleOpenCategoriaForm()}
              className="btn btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuova Categoria
            </button>
          </div>

          {loadingCategorie ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              {categorie.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Nessuna categoria trovata
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {categorie.map((categoria) => (
                    <div key={categoria.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-base font-medium text-gray-900">{categoria.nome}</h3>
                          {categoria.descrizione && (
                            <p className="text-sm text-gray-500 mt-1">{categoria.descrizione}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded ${
                            categoria.attivo === 1 || categoria.attivo === true ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {categoria.attivo === 1 || categoria.attivo === true ? 'Attiva' : 'Disattiva'}
                          </span>
                          <button
                            onClick={() => handleOpenCategoriaForm(categoria)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Modifica"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategoria(categoria)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Elimina definitivamente"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Modal Form Categoria */}
          <Modal
            isOpen={showCategoriaForm}
            onClose={handleCloseCategoriaForm}
            title={editingCategoria ? 'Modifica Categoria' : 'Nuova Categoria'}
          >
            <form onSubmit={handleSubmitCategoria} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Nome Categoria *
                </label>
                <input
                  type="text"
                  value={categoriaFormData.nome}
                  onChange={(e) => setCategoriaFormData({ ...categoriaFormData, nome: e.target.value })}
                  className="mt-1 input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Descrizione
                </label>
                <textarea
                  value={categoriaFormData.descrizione}
                  onChange={(e) => setCategoriaFormData({ ...categoriaFormData, descrizione: e.target.value })}
                  className="mt-1 input"
                  rows="3"
                />
              </div>
              {editingCategoria && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Stato
                    </label>
                    <p className="text-xs text-gray-500">
                      {categoriaFormData.attivo ? 'Attiva - visibile nelle tendine' : 'Disattiva - nascosta dalle tendine'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={categoriaFormData.attivo}
                      onChange={(e) => setCategoriaFormData({ ...categoriaFormData, attivo: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCloseCategoriaForm}
                  className="btn btn-outline"
                >
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCategoria ? 'Aggiorna' : 'Crea'}
                </button>
              </div>
            </form>
          </Modal>

          {/* Modal Eliminazione Categoria */}
          <Modal
            isOpen={showDeleteCategoriaModal}
            onClose={() => { setShowDeleteCategoriaModal(false); setCategoriaToDelete(null); }}
            title="Elimina Categoria"
            message={`Sei sicuro di voler eliminare definitivamente la categoria "${categoriaToDelete?.nome}"?\n\nQuesta azione non può essere annullata. La categoria verrà eliminata completamente dal database.`}
            type="warning"
            confirmText="Sì, Elimina"
            cancelText="Annulla"
            onConfirm={executeDeleteCategoria}
          />
        </div>
      )}

      {/* Tab Tipologie */}
      {activeTab === 'tipologie' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Tipologie Prodotto</h2>
            <button
              onClick={() => handleOpenTipologiaForm()}
              className="btn btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuova Tipologia
            </button>
          </div>

          {loadingTipologie ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              {tipologie.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Nessuna tipologia trovata
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {tipologie.map((tipologia) => (
                    <div key={tipologia.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-base font-medium text-gray-900">{tipologia.nome}</h3>
                          {tipologia.descrizione && (
                            <p className="text-sm text-gray-500 mt-1">{tipologia.descrizione}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded ${
                            tipologia.attivo === 1 || tipologia.attivo === true ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {tipologia.attivo === 1 || tipologia.attivo === true ? 'Attiva' : 'Disattiva'}
                          </span>
                          <button
                            onClick={() => handleOpenTipologiaForm(tipologia)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Modifica"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTipologia(tipologia)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Elimina definitivamente"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Modal Form Tipologia */}
          <Modal
            isOpen={showTipologiaForm}
            onClose={handleCloseTipologiaForm}
            title={editingTipologia ? 'Modifica Tipologia' : 'Nuova Tipologia'}
          >
            <form onSubmit={handleSubmitTipologia} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Nome Tipologia *
                </label>
                <input
                  type="text"
                  value={tipologiaFormData.nome}
                  onChange={(e) => setTipologiaFormData({ ...tipologiaFormData, nome: e.target.value })}
                  className="mt-1 input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Descrizione
                </label>
                <textarea
                  value={tipologiaFormData.descrizione}
                  onChange={(e) => setTipologiaFormData({ ...tipologiaFormData, descrizione: e.target.value })}
                  className="mt-1 input"
                  rows="3"
                />
              </div>
              {editingTipologia && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Stato
                    </label>
                    <p className="text-xs text-gray-500">
                      {tipologiaFormData.attivo ? 'Attiva - visibile nelle tendine' : 'Disattiva - nascosta dalle tendine'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tipologiaFormData.attivo}
                      onChange={(e) => setTipologiaFormData({ ...tipologiaFormData, attivo: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCloseTipologiaForm}
                  className="btn btn-outline"
                >
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTipologia ? 'Aggiorna' : 'Crea'}
                </button>
              </div>
            </form>
          </Modal>

          {/* Modal Eliminazione Tipologia */}
          <Modal
            isOpen={showDeleteTipologiaModal}
            onClose={() => { setShowDeleteTipologiaModal(false); setTipologiaToDelete(null); }}
            title="Elimina Tipologia"
            message={`Sei sicuro di voler eliminare definitivamente la tipologia "${tipologiaToDelete?.nome}"?\n\nQuesta azione non può essere annullata. La tipologia verrà eliminata completamente dal database.`}
            type="warning"
            confirmText="Sì, Elimina"
            cancelText="Annulla"
            onConfirm={executeDeleteTipologia}
          />
        </div>
      )}
    </div>
  );
}

export default CategorieTipologie;

